import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import { MenuCategory, MenuItem, MenuItemOption } from './types'

function generateSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'item'
  )
}

// ─── Categories ──────────────────────────────────────────────
export function useMenuCategories(restaurantId: string | undefined) {
  const [categories, setCategories] = useState<MenuCategory[]>([])
  const [loading, setLoading] = useState(true)

  const fetchCategories = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('menu_categories')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('sort_order', { ascending: true })

      if (error) {
        console.error('[NOIZA][menu] fetchCategories error:', error)
        throw error
      }
      setCategories(data || [])
    } catch (err) {
      console.error('[NOIZA][menu] fetchCategories exception:', err)
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const createCategory = async (data: {
    name: string
    description?: string
    is_active?: boolean
  }): Promise<{ success: boolean; error?: string }> => {
    console.log('[NOIZA][menu] === CREATE CATEGORY START ===')
    console.log('[NOIZA][menu] restaurantId at mutation time:', restaurantId)
    console.log('[NOIZA][menu] input data:', data)

    if (!restaurantId) {
      console.error(
        '[NOIZA][menu] CREATE CATEGORY BLOCKED — restaurantId is:',
        restaurantId,
      )
      return {
        success: false,
        error: 'No restaurant ID — restaurantId es undefined',
      }
    }

    try {
      const slug = generateSlug(data.name)
      const maxSort = categories.reduce(
        (max, c) => Math.max(max, c.sort_order),
        0,
      )

      const payload = {
        restaurant_id: restaurantId,
        name: data.name,
        slug,
        description: data.description || null,
        is_active: data.is_active ?? true,
        sort_order: maxSort + 1,
      }
      console.log(
        '[NOIZA][menu] createCategory INSERT payload:',
        JSON.stringify(payload, null, 2),
      )

      const response = await supabase
        .from('menu_categories')
        .insert(payload)
        .select()
        .single()

      console.log('[NOIZA][menu] createCategory FULL response:', {
        data: response.data,
        error: response.error,
        status: response.status,
        statusText: response.statusText,
      })

      if (response.error) {
        return {
          success: false,
          error: `Supabase: ${response.error.message} (code: ${response.error.code})`,
        }
      }

      console.log('[NOIZA][menu] createCategory SUCCESS — refetching...')
      await fetchCategories()
      return { success: true }
    } catch (err: any) {
      console.error('[NOIZA][menu] createCategory EXCEPTION:', err)
      return { success: false, error: err?.message || 'Error desconocido' }
    }
  }

  const updateCategory = async (
    id: string,
    data: Partial<{
      name: string
      description: string | null
      is_active: boolean
      sort_order: number
    }>,
  ): Promise<{ success: boolean; error?: string }> => {
    console.log('[NOIZA][menu] === UPDATE CATEGORY START ===')
    console.log('[NOIZA][menu] target id:', id)
    console.log('[NOIZA][menu] input data:', data)

    try {
      const updateData: any = { ...data }
      if (data.name) {
        updateData.slug = generateSlug(data.name)
      }
      console.log(
        '[NOIZA][menu] updateCategory UPDATE payload:',
        JSON.stringify(updateData, null, 2),
      )

      const response = await supabase
        .from('menu_categories')
        .update(updateData)
        .eq('id', id)

      console.log('[NOIZA][menu] updateCategory FULL response:', {
        data: response.data,
        error: response.error,
        status: response.status,
        statusText: response.statusText,
      })

      if (response.error) {
        return {
          success: false,
          error: `Supabase: ${response.error.message} (code: ${response.error.code})`,
        }
      }

      console.log('[NOIZA][menu] updateCategory SUCCESS — refetching...')
      await fetchCategories()
      return { success: true }
    } catch (err: any) {
      console.error('[NOIZA][menu] updateCategory EXCEPTION:', err)
      return { success: false, error: err?.message || 'Error desconocido' }
    }
  }

  const toggleCategory = async (id: string, isActive: boolean) => {
    return updateCategory(id, { is_active: isActive })
  }

  return {
    categories,
    loading,
    createCategory,
    updateCategory,
    toggleCategory,
    refetch: fetchCategories,
  }
}

// ─── Menu Items ──────────────────────────────────────────────
export function useMenuItems(restaurantId: string | undefined) {
  const [items, setItems] = useState<MenuItem[]>([])
  const [loading, setLoading] = useState(true)

  const fetchItems = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('sort_order', { ascending: true })

      if (error) {
        console.error('[NOIZA][menu] fetchItems error:', error)
        throw error
      }
      setItems(data || [])
    } catch (err) {
      console.error('[NOIZA][menu] fetchItems exception:', err)
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  const createItem = async (data: {
    name: string
    description?: string
    price: number
    category_id: string
    station_code: string | null
    is_active?: boolean
    is_shareable?: boolean
    image_url?: string | null
  }): Promise<{ success: boolean; error?: string }> => {
    console.log('[NOIZA][menu] === CREATE ITEM START ===')
    console.log('[NOIZA][menu] restaurantId at mutation time:', restaurantId)
    console.log('[NOIZA][menu] input data:', data)

    if (!restaurantId) {
      console.error(
        '[NOIZA][menu] CREATE ITEM BLOCKED — restaurantId is:',
        restaurantId,
      )
      return {
        success: false,
        error: 'No restaurant ID — restaurantId es undefined',
      }
    }

    try {
      const catItems = items.filter((i) => i.category_id === data.category_id)
      const maxSort = catItems.reduce(
        (max, i) => Math.max(max, i.sort_order),
        0,
      )
      const slug = generateSlug(data.name)

      const payload = {
        restaurant_id: restaurantId,
        category_id: data.category_id,
        name: data.name,
        slug,
        description: data.description || null,
        price: data.price,
        station_code: data.station_code,
        is_active: data.is_active ?? true,
        is_shareable: data.is_shareable ?? false,
        image_url: data.image_url || null,
        sort_order: maxSort + 1,
        is_customizable: false,
        item_type: null,
      }
      console.log(
        '[NOIZA][menu] createItem INSERT payload:',
        JSON.stringify(payload, null, 2),
      )

      const response = await supabase
        .from('menu_items')
        .insert(payload)
        .select()
        .single()

      console.log('[NOIZA][menu] createItem FULL response:', {
        data: response.data,
        error: response.error,
        status: response.status,
        statusText: response.statusText,
      })

      if (response.error) {
        return {
          success: false,
          error: `Supabase: ${response.error.message} (code: ${response.error.code})`,
        }
      }

      console.log('[NOIZA][menu] createItem SUCCESS — refetching...')
      await fetchItems()
      return { success: true }
    } catch (err: any) {
      console.error('[NOIZA][menu] createItem EXCEPTION:', err)
      return { success: false, error: err?.message || 'Error desconocido' }
    }
  }

  const updateItem = async (
    id: string,
    data: Partial<{
      name: string
      description: string | null
      price: number
      category_id: string
      station_code: string | null
      is_active: boolean
      is_shareable: boolean
      image_url: string | null
      sort_order: number
    }>,
  ): Promise<{ success: boolean; error?: string }> => {
    console.log('[NOIZA][menu] === UPDATE ITEM START ===')
    console.log('[NOIZA][menu] target id:', id)
    console.log('[NOIZA][menu] input data:', JSON.stringify(data, null, 2))

    try {
      const updateData: any = { ...data }
      if (data.name) {
        updateData.slug = generateSlug(data.name)
      }
      console.log(
        '[NOIZA][menu] updateItem UPDATE payload:',
        JSON.stringify(updateData, null, 2),
      )

      const response = await supabase
        .from('menu_items')
        .update(updateData)
        .eq('id', id)

      console.log('[NOIZA][menu] updateItem FULL response:', {
        data: response.data,
        error: response.error,
        status: response.status,
        statusText: response.statusText,
      })

      if (response.error) {
        return {
          success: false,
          error: `Supabase: ${response.error.message} (code: ${response.error.code})`,
        }
      }

      console.log('[NOIZA][menu] updateItem SUCCESS — refetching...')
      await fetchItems()
      return { success: true }
    } catch (err: any) {
      console.error('[NOIZA][menu] updateItem EXCEPTION:', err)
      return { success: false, error: err?.message || 'Error desconocido' }
    }
  }

  const toggleItem = async (id: string, isActive: boolean) => {
    return updateItem(id, { is_active: isActive })
  }

  const deleteItem = async (
    id: string,
  ): Promise<{ success: boolean; error?: string }> => {
    console.log('[NOIZA][menu] === DELETE ITEM START ===')
    console.log('[NOIZA][menu] target id:', id)

    try {
      const response = await supabase.from('menu_items').delete().eq('id', id)

      console.log('[NOIZA][menu] deleteItem FULL response:', {
        data: response.data,
        error: response.error,
        status: response.status,
        statusText: response.statusText,
      })

      if (response.error) {
        return {
          success: false,
          error: `Supabase: ${response.error.message} (code: ${response.error.code})`,
        }
      }

      console.log('[NOIZA][menu] deleteItem SUCCESS — refetching...')
      await fetchItems()
      return { success: true }
    } catch (err: any) {
      console.error('[NOIZA][menu] deleteItem EXCEPTION:', err)
      return { success: false, error: err?.message || 'Error desconocido' }
    }
  }

  return {
    items,
    loading,
    createItem,
    updateItem,
    toggleItem,
    deleteItem,
    refetch: fetchItems,
  }
}

// ─── Item Options ────────────────────────────────────────────
export function useMenuItemOptions(menuItemId: string | undefined) {
  const [options, setOptions] = useState<MenuItemOption[]>([])
  const [loading, setLoading] = useState(true)

  const fetchOptions = useCallback(async () => {
    if (!menuItemId) {
      setOptions([])
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('menu_item_options')
        .select('*')
        .eq('menu_item_id', menuItemId)
        .order('sort_order', { ascending: true })

      if (error) throw error
      setOptions(data || [])
    } catch (err) {
      console.error('[NOIZA][menu] fetchOptions error:', err)
    } finally {
      setLoading(false)
    }
  }, [menuItemId])

  useEffect(() => {
    fetchOptions()
  }, [fetchOptions])

  const createOption = async (data: {
    restaurant_id: string
    option_group: string
    name: string
    price_delta: number
    is_required?: boolean
    max_select?: number
  }): Promise<{ success: boolean; error?: string }> => {
    console.log('[NOIZA][menu] === CREATE OPTION START ===')
    console.log('[NOIZA][menu] menuItemId:', menuItemId)
    console.log('[NOIZA][menu] input data:', data)

    if (!menuItemId) return { success: false, error: 'No menu item ID' }

    try {
      const maxSort = options.reduce((max, o) => Math.max(max, o.sort_order), 0)
      const payload = {
        menu_item_id: menuItemId,
        restaurant_id: data.restaurant_id,
        option_group: data.option_group,
        name: data.name,
        price_delta: data.price_delta,
        is_required: data.is_required ?? false,
        max_select: data.max_select ?? 1,
        is_active: true,
        sort_order: maxSort + 1,
      }
      console.log(
        '[NOIZA][menu] createOption INSERT payload:',
        JSON.stringify(payload, null, 2),
      )

      const response = await supabase
        .from('menu_item_options')
        .insert(payload)
        .select()
        .single()

      console.log('[NOIZA][menu] createOption FULL response:', {
        data: response.data,
        error: response.error,
        status: response.status,
        statusText: response.statusText,
      })

      if (response.error) {
        return {
          success: false,
          error: `Supabase: ${response.error.message} (code: ${response.error.code})`,
        }
      }

      await fetchOptions()
      return { success: true }
    } catch (err: any) {
      console.error('[NOIZA][menu] createOption EXCEPTION:', err)
      return { success: false, error: err?.message || 'Error desconocido' }
    }
  }

  const updateOption = async (
    id: string,
    data: Partial<{
      name: string
      option_group: string
      price_delta: number
      is_required: boolean
      max_select: number
      is_active: boolean
      depends_on_group: string | null
      depends_on_option_values: string[] | null
    }>,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('[NOIZA][menu] updateOption payload:', { id, ...data })
      const response = await supabase
        .from('menu_item_options')
        .update(data)
        .eq('id', id)

      if (response.error) {
        return { success: false, error: `Supabase: ${response.error.message}` }
      }

      await fetchOptions()
      return { success: true }
    } catch (err: any) {
      console.error('[NOIZA][menu] updateOption EXCEPTION:', err)
      return { success: false, error: err?.message || 'Error desconocido' }
    }
  }

  const deleteOption = async (
    id: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('[NOIZA][menu] deleteOption:', id)
      const response = await supabase
        .from('menu_item_options')
        .delete()
        .eq('id', id)

      if (response.error) {
        return { success: false, error: `Supabase: ${response.error.message}` }
      }

      await fetchOptions()
      return { success: true }
    } catch (err: any) {
      console.error('[NOIZA][menu] deleteOption EXCEPTION:', err)
      return { success: false, error: err?.message || 'Error desconocido' }
    }
  }

  return {
    options,
    loading,
    createOption,
    updateOption,
    deleteOption,
    refetch: fetchOptions,
  }
}

// ─── POS Internal Products ───────────────────────────────────
export function usePOSInternalProducts(restaurantId: string | undefined) {
  const [items, setItems] = useState<MenuItem[]>([])
  const [loading, setLoading] = useState(true)

  const fetchItems = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('internal_only', true)
        .order('name', { ascending: true })

      if (error) throw error
      setItems(data || [])
    } catch (err) {
      console.error('[NOIZA][pos] fetchItems error:', err)
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  return {
    items,
    loading,
    refetch: fetchItems,
  }
}

// ─── POS Retail Products (NatuCafé Caja) ─────────────────────
// Fetches menu_items flagged as retail/Caja products for the POS/Caja
// module. Distinct from usePOSInternalProducts (which keys off the
// legacy internal_only flag) — this hook uses the newer columns
// (product_type='retail', show_in_pos=true) introduced for NatuCafé.
// Safe to use alongside the existing menu hooks: it only reads.
export function usePOSRetailProducts(restaurantId: string | undefined) {
  const [items, setItems] = useState<MenuItem[]>([])
  const [loading, setLoading] = useState(true)

  const fetchItems = useCallback(async () => {
    if (!restaurantId) {
      setItems([])
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('product_type', 'retail')
        .eq('show_in_pos', true)
        .or('is_active.is.null,is_active.eq.true')
        .order('name', { ascending: true })

      if (error) throw error
      setItems((data || []) as MenuItem[])
    } catch (err) {
      console.error('[NOIZA][pos-retail] fetchItems error:', err)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  return {
    items,
    loading,
    refetch: fetchItems,
  }
}
